import { XYRTC } from "./client";
import Logger from "./tools/logger";
import { throttle } from "./tools/utils";
import { DeviceTypeMap, IDeviceItem, IDeviceList, ICurrentDevice, DeviceTypeKey } from "./type";
import { EVENT } from "./tools/enum";

export default class DeviceManager {
  private sdk: XYRTC;
  private logger: Logger;
  private deviceList: IDeviceList;
  private currentDevice: ICurrentDevice;
  private prevDevice: ICurrentDevice; // 记录上一次

  private onCameraChange: (devType: DeviceTypeMap) => void;
  private onMicrophoneChange: (devType: DeviceTypeMap) => void;
  private onSpeakerChange: (devType: DeviceTypeMap) => void;

  constructor(sdk: XYRTC, logger: Logger) {
    this.sdk = sdk;
    this.logger = logger;

    this.deviceList = {
      camera: [],
      microphone: [],
      speaker: []
    };

    this.currentDevice = {
      camera: '',
      microphone: '',
      speaker: ''
    }

    this.prevDevice = { ...this.currentDevice };

    this.onDeviceChange = this.onDeviceChange.bind(this);
    this.onCameraChange = throttle(this.onDeviceChange, 1000);
    this.onMicrophoneChange = throttle(this.onDeviceChange, 1000);
    this.onSpeakerChange = throttle(this.onDeviceChange, 1000);

    this.initDevice();
  }

  /**
   * 设备管理初始化，获取设备列表和设置当前使用设备
   *
   * @private
   * @return {*}
   * @memberof DeviceManager
   */
  private initDevice() {
    return new Promise((resolve, reject) => {
      const cameraList: IDeviceItem[] = this.sdk.getDevListEx(DeviceTypeMap.camera);
      const microphoneList: IDeviceItem[] = this.sdk.getDevListEx(DeviceTypeMap.microphone);
      const speakerList: IDeviceItem[] = this.sdk.getDevListEx(DeviceTypeMap.speaker);

      this.deviceList = {
        camera: cameraList,
        microphone: microphoneList,
        speaker: speakerList
      }

      this.logger.log('initDevice deviceList', this.deviceList);

      this.sdk.emit(EVENT.DEVICE, this.deviceList);

      const currentCamera = cameraList.find(item => item.isSelected);
      const currentMicrophone = microphoneList.find(item => item.isSelected);
      const currentSpeaker = speakerList.find(item => item.isSelected);

      this.currentDevice = {
        camera: currentCamera.devId,
        microphone: currentMicrophone.devId,
        speaker: currentSpeaker.devId
      }

      this.logger.log('initDevice currentDevice', this.currentDevice);

      this.sdk.chooseDevEx(DeviceTypeMap.microphone, currentMicrophone.devId);
      this.sdk.chooseDevEx(DeviceTypeMap.speaker, currentSpeaker.devId);

      this.sdk.emit(EVENT.CURRENT_DEVICE, this.currentDevice);

      resolve(null);
    }).catch(e => {
      this.logger.log('initDevice error', e)
    })
  }

  /**
   * 获取设备列表
   *
   * @param {DeviceTypeKey} [type]
   * @return {*}
   * @memberof DeviceManager
   */
  public getDeviceList(type?: DeviceTypeKey) {
    return type ? this.deviceList[type] : this.deviceList;
  }

  /**
   * 获取当前使用的设备
   *
   * @param {DeviceTypeKey} [type]
   * @return {*}
   * @memberof DeviceManager
   */
  public getCurrentDevice(type?: DeviceTypeKey) {
    return type ? this.currentDevice[type] : this.currentDevice;
  }

  /**
   * 切换设备
   *
   * @param {DeviceTypeKey} type
   * @param {string} devId
   * @memberof DeviceManager
   */
  public switchDevice(type: DeviceTypeKey, devId: string) {
    this.sdk.chooseDevEx(DeviceTypeMap[type], devId);

    this.prevDevice = { ...this.prevDevice, [type]: this.currentDevice[type] };

    this.currentDevice = { ...this.currentDevice, [type]: devId };

    this.logger.log('switchDevice type', type);
    this.logger.log('switchDevice prevDevice currentDevice', this.prevDevice[type], this.currentDevice[type]);

    this.sdk.emit(EVENT.CURRENT_DEVICE, { [type]: devId });
  }

  /**
   * 监听设备改变
   *
   * @param {DeviceTypeMap} devType
   * @memberof DeviceManager
   */
  public onMediaDeviceEvent(devType: DeviceTypeMap) {
    if (devType === DeviceTypeMap.camera) {
      this.onCameraChange(devType)
    }
    if (devType === DeviceTypeMap.microphone) {
      this.onMicrophoneChange(devType)
    }
    if (devType === DeviceTypeMap.speaker) {
      this.onSpeakerChange(devType)
    }
  }

  /**
   * 设备改变处理
   *
   * @private
   * @param {DeviceTypeMap} devType
   * @memberof DeviceManager
   */
  private onDeviceChange(devType: DeviceTypeMap) {
    const type = DeviceTypeMap[devType] as DeviceTypeKey;
    const oldDevList = this.deviceList[type];

    this.logger.log('onDeviceChange type', type);

    const devList: IDeviceItem[] = this.sdk.getDevListEx(devType);

    this.deviceList = { ...this.deviceList, [type]: devList };
    this.sdk.emit(EVENT.DEVICE, { [type]: devList });

    this.logger.log('onDeviceChange oldDevList newDevList: ', oldDevList, devList)

    this.diffDeviceList(devList, oldDevList, devType);
  }

  /**
   * diff设备列表，找出改变的设备，并切换
   *
   * @private
   * @param {IDeviceItem[]} newDevList 新列表
   * @param {IDeviceItem[]} oldDevList 旧列表
   * @param {DeviceTypeMap} devType 设备改变
   * @memberof DeviceManager
   */
  private diffDeviceList(newDevList: IDeviceItem[], oldDevList: IDeviceItem[], devType: DeviceTypeMap) {

    const type = DeviceTypeMap[devType] as DeviceTypeKey;

    const changeLen = newDevList.length - oldDevList.length;

    this.logger.log('diffDeviceList type and changeLen', type, changeLen);

    if (changeLen > 0) {
      // 增加了设备，勾选增加的设备
      const addItem = newDevList.find(newDevice => oldDevList.every(oldDevice => oldDevice.devId !== newDevice.devId));

      this.sdk.switchDeviceSync(type, addItem.devId);
    }

    if (changeLen < 0) {
      // 移除了设备，查看当前勾选的设备是否被移除了
      const isExistSelected = newDevList.findIndex(newDevice => newDevice.devId === this.currentDevice[type]);

      if (isExistSelected === -1) {
        const isExistPrevSelected = this.prevDevice[type] && this.deviceList[type].some(device => device.devId === this.prevDevice[type]);

        // 优先勾选上一次选中的
        const devId = isExistPrevSelected ? this.prevDevice[type] : this.deviceList[type][0]?.devId;

        this.sdk.switchDeviceSync(type, devId);
      }
    }

    // 可能设备列表没增没减，但要判断是否设备改变了(例如某些35mm耳机)，如果改变，也要勾选上, 但是目前没法判断插入还是拔出，先忽略
    // if (changeLen === 0) {
    //   const changedItem = newDevList.find(newDevice => oldDevList.every(oldDevice => oldDevice.devId !== newDevice.devId || oldDevice.devName !== newDevice.devName));

    //   if (changedItem) {
    //     if (devEvent === DeviceEventMap.Added && changedItem.devId!==this.currentDevice[type]) {
    //       this.sdk.switchDeviceSync(type, changedItem.devId);
    //     }

    //     if (devEvent === DeviceEventMap.Removed || devEvent === DeviceEventMap.Lost) {
    //       const isExistPrevSelected = this.prevDevice[type] && this.deviceList[type].some(device => device.devId === this.prevDevice[type]);
    //       const devId = isExistPrevSelected ? this.prevDevice[type] : this.deviceList[type][0].devId;

    //       this.sdk.switchDeviceSync(type, devId);
    //     }
    //   }
    // }
  }
}